import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useSubscription } from "@/hooks/useSubscription";
import { PaywallModal } from "@/components/PaywallModal";
import { CategoryIcon } from "@/components/CategoryIcon";
import { formatTimeAgo } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { toast } from "sonner";
import { MapPin, Zap, Loader2 } from "lucide-react";

export function ProFeed() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [offer, setOffer] = useState({ price: "", eta_minutes: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const { isPro, isLoading: subLoading } = useSubscription();

  const { data: requests, isLoading, error } = useQuery({
    queryKey: ["pro-requests"],
    queryFn: async () => (await api.get("/pro/requests")).data,
    refetchInterval: 30000,
    retry: false,
  });

  const unverified = error?.response?.status === 403;
  const unverifiedReason = error?.response?.data?.detail || "";

  const sendOffer = async () => {
    if (!offer.price || !offer.eta_minutes) { toast.error("Inserisci prezzo e tempo di arrivo"); return; }
    setBusy(true);
    try {
      await api.post(`/requests/${selected.id}/offers`, {
        price: Number(offer.price),
        eta_minutes: Number(offer.eta_minutes),
        notes: offer.notes,
      });
      toast.success("Offerta inviata!");
      setSelected(null);
      setOffer({ price: "", eta_minutes: "", notes: "" });
      qc.invalidateQueries({ queryKey: ["pro-requests", "pro-offers"] });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="px-4 sm:px-6 pt-6 sm:pt-10 space-y-4" data-testid="pro-feed-page">
      <div className="space-y-1">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Richieste per te</h1>
        <p className="text-sm text-slate-600">Compatibili con le tue categorie e la tua zona.</p>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-3xl" />)}</div>
      ) : unverified ? (
        <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 text-center space-y-2" data-testid="unverified-banner">
          <p className="text-sm font-semibold text-amber-800">Profilo non ancora operativo</p>
          <p className="text-xs text-amber-700" data-testid="unverified-reason">{unverifiedReason || "Il tuo profilo è in attesa di approvazione da parte di un amministratore."}</p>
        </div>
      ) : requests?.length ? (
        <>
          {!isPro && (
            <div className="bg-slate-900 rounded-3xl p-4 flex items-center justify-between gap-3" data-testid="free-pro-banner">
              <p className="text-sm text-slate-200">
                <span className="font-bold text-white">{requests.length} richieste compatibili nella tua zona.</span> Vedi zona, categoria e urgenza: con Pro sblocchi dettagli completi e offerte.
              </p>
              <Button data-testid="banner-upgrade-btn" size="sm" onClick={() => setPaywall(true)} className="rounded-full bg-whoo hover:bg-whoo-dark text-white font-semibold shrink-0">
                Passa a Pro
              </Button>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="pro-requests-list">
          {requests.map((r) => (
            <div key={r.id} data-testid={`feed-request-${r.id}`} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-3">
              <div className="flex items-start gap-2">
                <span className="w-9 h-9 rounded-xl bg-whoo-soft text-whoo flex items-center justify-center shrink-0">
                  <CategoryIcon icon={r.category} className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`font-semibold text-slate-900 text-sm leading-snug ${!isPro ? "blur-[4px] select-none" : ""}`}>{r.title || r.raw_text}</p>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-1">
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{r.zone || r.address || "—"}</span>
                    {r.distance_km != null && <span>{r.distance_km.toFixed(1)} km</span>}
                    <span>{formatTimeAgo(r.created_at)}</span>
                    {r.urgent && <span className="text-whoo font-bold flex items-center gap-1"><Zap className="w-3.5 h-3.5" />Urgente</span>}
                  </div>
                </div>
              </div>
              <Button data-testid={`send-offer-btn-${r.id}`} onClick={() => subLoading ? null : isPro ? setSelected(r) : setPaywall(true)}
                className="w-full rounded-full bg-whoo hover:bg-whoo-dark text-white font-semibold active:scale-[0.98] transition-transform">
                {isPro ? "Invia offerta" : "Invia offerta (Pro)"}
              </Button>
            </div>
          ))}
          </div>
        </>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 text-center" data-testid="no-compatible-requests">
          <p className="text-sm text-slate-500">Nessuna richiesta compatibile al momento. Attiva "Disponibile ora" per ricevere notifiche.</p>
        </div>
      )}

      <PaywallModal open={paywall} onClose={() => setPaywall(false)} reason="Inviare offerte ai clienti è una funzione Pro." />

      <Drawer open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DrawerContent className="max-w-md mx-auto rounded-t-3xl">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-heading">Offerta per: {selected?.title || selected?.raw_text?.slice(0, 60)}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-8 space-y-4" data-testid="offer-drawer">
            <div className="space-y-2">
              <Label htmlFor="price">Prezzo (€)</Label>
              <Input id="price" data-testid="offer-price-input" type="number" min="0" step="0.01" value={offer.price}
                onChange={(e) => setOffer((o) => ({ ...o, price: e.target.value }))}
                className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="Es. 80" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eta">Arrivo stimato (minuti)</Label>
              <Input id="eta" data-testid="offer-eta-input" type="number" min="1" max="600" value={offer.eta_minutes}
                onChange={(e) => setOffer((o) => ({ ...o, eta_minutes: e.target.value }))}
                className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="Es. 30" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Note (opzionale)</Label>
              <Textarea id="notes" data-testid="offer-notes-input" rows={3} value={offer.notes}
                onChange={(e) => setOffer((o) => ({ ...o, notes: e.target.value }))}
                className="bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl resize-none" placeholder="Es. Chiamata e sopralluogo inclusi" />
            </div>
            <Button data-testid="submit-offer-btn" onClick={sendOffer} disabled={busy}
              className="w-full h-14 rounded-full bg-whoo hover:bg-whoo-dark text-white font-heading font-semibold active:scale-[0.98] transition-transform">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia offerta"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
